import React from 'react'
import { Button, Grid, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";

import { useAuth } from "hooks";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";
import { useLazyGetDDLCollectionListQuery } from "store/services/collectionApi";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

const INITIAL_STATE = {
  account: "",
  entity: "",
  shipmentId: "",
  collectionId: "",
  loadingTime: "",
  unloadingTime: "",
  loadingLocation: "",
  unloadingLocation: "",
  cargoType: "",
  cargoCondition: "",
  isActive: false,
  transshipmentType: { label: "Loading", id: "loading" }

}
const TransshipmentForm = ({
  isBusy = null,
  transshipmentDetail = null,
  onSubmit = () => { },
}) => {

  const navigate = useNavigate();

  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // react hook form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,

  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const transshipmentType = watch("transshipmentType");
  const { data: cargoTypes = [] } =
    useListListQuery({
      type: ENTITY_LIST_CONSTANTS.CARGO_TYPES,
      status: "Active",
    });
  const { data: cargoConditions = [] } =
    useListListQuery({
      type: ENTITY_LIST_CONSTANTS.CARGO_CONDITIONS,
      status: "Active",
    });
  
    const _mappedAccountTypeList = Array.isArray(cargoTypes)
    ? cargoTypes.map((item) => ({
        label: item?.name, 
        id: item?._id,
      }))
    : [];
    const _mappedCargoConditionTypeList = Array.isArray(cargoConditions)
    ? cargoConditions.map((item) => ({
        label: item?.name, 
        id: item?._id,
      }))
    : [];
  // RTK Query
  const
    {
      data: accounts,
      isLoading: loadingAccounts,
    }
      = useGetDDLAccountListQuery();

  // RTK Query
  const [
    fetchEntities,
    {
      data: entities,
      isLoading: loadingEntities,
    }]
    = useLazyGetDDLEntityListQuery();

  const [
    fetchShipments,
    { data: shipments, isLoading: loadingShipments }
  ] = useLazyGetDDLShipmentListQuery();

  const [
    fetchShipmentFulfillments,
    { data: shipmentFulfillments, isLoading: loadingShipmentFulfillments }
  ] = useLazyGetDDLCollectionListQuery();

  // methods
  const handleOnTransshipmentSubmit = (data) => {
    const _transshipmentPayload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      shipmentId: data?.shipmentId?._id,
      transshipmentType: data.transshipmentType,
    }
    onSubmit(_transshipmentPayload);
  };

  React.useEffect(() => {
    if (!transshipmentDetail) return;
    reset({
      ...transshipmentDetail,
      transshipmentType: transshipmentDetail?.transshipmentType ?? { label: "Loading", id: "loading" },
      loadingTime: transshipmentDetail?.loadingTime
        ? new Date(transshipmentDetail.loadingTime).toISOString().slice(0, 16)
        : "",
      unloadingTime: transshipmentDetail?.unloadingTime
        ? new Date(transshipmentDetail.unloadingTime).toISOString().slice(0, 16)
        : "",

    })
  }, [reset, transshipmentDetail, transshipmentType]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({ account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id });
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return
    fetchShipments({ account: _watchAccount?._id, entity: _watchEntity?._id })
  }, [_watchAccount?._id, _watchEntity?._id, fetchShipments, viewEntity])

  React.useEffect(() => {
    if (!_watchEntity?._id) return
    fetchShipmentFulfillments({ account: _watchAccount?._id, entity: _watchEntity?._id })
  }, [_watchAccount?._id, _watchEntity?._id, fetchShipmentFulfillments, viewEntity])

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Controller
              name="transshipmentType"
              control={control}
              rules={{ required: "Please select a type" }}
              render={({ field: { onChange, value, ...field } }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  required
                  label="Transshipment Type"
                  options={[
                    { label: "Loading", id: "loading" },
                    { label: "Unloading", id: "unloading" },
                  ]}
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(_, data) => onChange(data.id)}
                />
              )}
            />
          </Grid>

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "")

                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("shipmentId", "")
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Shipment ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);

                  }}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={shipments || []}
                  loading={loadingShipments}
                  loadingText="Loading ShipmentFD..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="collectionId"
              control={control}
              rules={{ required: "Please select Collection ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Collection ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);

                  }}
                  getOptionLabel={(option) => option?.collectionId ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={shipmentFulfillments || []}
                  loading={loadingShipmentFulfillments}
                  loadingText="Loading Collection..."
                  helperText={errors?.collectionId?.message}
                  error={!!errors?.collectionId}
                />
              )}
            />
          </Grid>
          {transshipmentType === "loading" && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="loadingLocation.lat"
                  control={control}
                  rules={{ required: "Loading Latitude is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Loading Latitude"
                      type="number"
                      error={!!errors?.loadingLocation?.lat}
                      helperText={errors?.loadingLocation?.lat?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="loadingLocation.lon"
                  control={control}
                  rules={{ required: "Loading Longitude is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Loading Longitude"
                      type="number"
                      error={!!errors?.loadingLocation?.lon}
                      helperText={errors?.unloadingLocation?.lon?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="loadingTime"
                  control={control}
                  rules={{ required: "Loading Time is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Loading Time"
                      type="datetime-local"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true, }}
                      error={!!errors.loadingTime}
                      helperText={errors?.loadingTime?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {transshipmentType === "unloading" && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="unloadingLocation.lat"
                  control={control}
                  rules={{ required: "UnLoading Latitude is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="UnLoading Latitude"
                      type="number"
                      error={!!errors?.unloadingLocation?.lat}
                      helperText={errors?.unloadingLocation?.lat?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="unloadingLocation.lon"
                  control={control}
                  rules={{ required: "UnLoading Longitude is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="UnLoading Longitude"
                      type="number"
                      error={!!errors?.unloadingLocation?.lon}
                      helperText={errors?.unloadingLocation?.lon?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="unloadingTime"
                  control={control}
                  rules={{ required: "UnLoading Time is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="UnLoading Time"
                      type="datetime-local"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true, }}
                      error={!!errors.unloadingTime}
                      helperText={errors?.unloadingTime?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <Controller
              name="cargoType"
              control={control}
              rules={{ required: "Add cargoType" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Cargo Type"
                  options={_mappedAccountTypeList?.map((option) => option.label) ?? []}                 
                  />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="cargoCondition"
              control={control}
              rules={{ required: "Add cargo condition" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Cargo Condition"
                  options={_mappedCargoConditionTypeList?.map((option) => option.label) ?? []}                 
                />
              )}
            />
          </Grid>

          {transshipmentDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>

      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained"
          onClick={handleSubmit(handleOnTransshipmentSubmit)}
        >
          {transshipmentDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>

  )
}

export default TransshipmentForm;